import { AppDispatch } from "@Reducers/index";
import { getBackendUrl } from "@Utils";
import axiosWrapper from "@AxiosWrapper";
import type { Exact } from "@Helpers/types/exact";
import type { StatusResponse, ObjectIdString } from "@Helpers/schemas/common.schema";
import { SOMETHING_WENT_WRONG, TOAST_TYPE_ERROR, TOAST_TYPE_SUCCESS } from "@Services/constants";
import type {
  SaveProjectsRequest,
  SaveProjectsResponse,
  SaveProjectStatusRequest,
  SaveProjectStatusResponse,
  SaveProjectAssignedPositionResponse,
  GetAllStatusesForProjectsResponse,
} from "@ServerTypes/projects/project.schema";
import type {
  SaveTaskStatusRequest,
  SaveTaskStatusResponse,
  DeleteTaskRequest,
  DeleteTaskResponse,
} from "@ServerTypes/tasks/tasks.schema";
import type {
  GetPdfMarkUpDataRequest,
  GetPdfMarkUpDataResponse,
  GetIssuesIdToExportRequest,
  GetIssuesIdToExportResponse,
  SaveIssuesDataFromBCFRequest,
  SaveIssuesDataFromBCFResponse,
  GetIssuesDataRequest,
  GetIssuesDataResponse,
  IssueItem,
  GetIssuesPinDataRequest,
  GetIssuesPinDataResponse,
  GetIssueViewpointDataRequest,
  GetIssueViewpointDataResponse,
  GetIssuesDataByPortalRequest,
  GetIssuesDataByPortalResponse,
  RemoveIssueFromFormRequest,
  RemoveIssueFromFormResponse,
  AddFormIdToIssuesRequest,
  AddFormIdToIssuesResponse,
  UpdateIssueCustomFieldsRequest,
  UpdateIssueCustomFieldsResponse,
} from "@ServerTypes/issues/issues.schema";
import type { AxiosResponse } from "axios";
import type { FetchDefaultListOfStatusesAvailableResponse } from "@ServerTypes/reviews/reviews.schema";
import type { GetIssueCommentsRequest, GetCommentsResponse } from "@ServerTypes/comments/comments.schema.js";
import { defaultListOfStatusAvailableUrl } from "@Services/dataManager";
import { showToastNotification } from "@Reducers/toastSlice";
import { setIssuesCount } from "./issuesCount";
import { setProjectsStatusesData } from "@Reducers/projectsStatuses";
import { setItemsHaveError, setItemsAreLoading } from "./bcfData";
import { setIssuesPinData } from "./issuesPinDataSlice";
import {
  updateIssuesData as arrangeIssuesData,
  setIssuesData,
  modifyIssuesData,
  mergeIssuesData as _mergeIssuesData,
  removeIssueFromState,
} from "./issues";
import { t, untranslated } from "@i18n";
import { EXPORT_TYPE, exportPdf } from "@Services/fileDownload";
import { errorCodes } from "@Helpers/errorCodes";
import _ from "lodash";
import * as Sentry from "@sentry/react";
interface ExportParams {
  projectId: ObjectIdString;
  issuesIds?: ObjectIdString[];
}

/**
 * Metadata-only update of an issue's standard/custom field answers from the detail sidebar.
 * The server validates only the isMetadata fields before persisting the edited formTemplate.
 */
export const updateIssueCustomFieldsRequest = (
  data: UpdateIssueCustomFieldsRequest,
): Promise<AxiosResponse<UpdateIssueCustomFieldsResponse>> =>
  axiosWrapper.post<UpdateIssueCustomFieldsResponse, UpdateIssueCustomFieldsRequest>(
    `${getBackendUrl()}/issues/updateIssueCustomFields`,
    data,
  );

export function removeIssueFromForm(
  ids: RemoveIssueFromFormRequest["ids"],
  formId: RemoveIssueFromFormRequest["formId"],
) {
  return async (dispatch: AppDispatch) => {
    const data: RemoveIssueFromFormRequest = { ids, formId };
    const response = await axiosWrapper.post<RemoveIssueFromFormResponse>(
      `${getBackendUrl()}/issues/removeIssueFromForm`,
      data,
    );
    if (response.data.status) {
      dispatch(removeIssueFromState({ ids }));
      dispatch(
        showToastNotification({
          content: t("All the selected Issue(s) were removed"),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
    } else {
      dispatch(
        showToastNotification({
          content: SOMETHING_WENT_WRONG,
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
    return response.data.status;
  };
}

export function addFormIdToIssues(ids: AddFormIdToIssuesRequest["ids"], formId: AddFormIdToIssuesRequest["formId"]) {
  return async (dispatch: AppDispatch) => {
    const data: AddFormIdToIssuesRequest = { ids, formId };
    const response = await axiosWrapper.post<AddFormIdToIssuesResponse>(
      `${getBackendUrl()}/issues/addFormIdToIssues`,
      data,
    );
    if (response.data.status) {
      dispatch(
        showToastNotification({
          content: t("All the selected Issue(s) were added"),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
    } else {
      dispatch(
        showToastNotification({
          content: SOMETHING_WENT_WRONG,
          type: TOAST_TYPE_ERROR,
        }),
      );
    }
  };
}

export function updateTaskData(data: Record<string, unknown>) {
  return {
    type: "UPDATE_TASK_DATA",
    data,
  };
}
export function removeTaskData(data: Record<string, unknown>) {
  return {
    type: "REMOVE_TASK_DATA",
    data,
  };
}

export const issuesInFilesCount = async <T extends Exact<GetIssuesDataRequest, T>>(data: T) => {
  const response = await axiosWrapper.post<GetIssuesDataResponse>(`${getBackendUrl()}/issues/getIssuesData`, data);
  if (!response.data.status) return 0;
  return response.data.result?.length ?? 0;
};

export function getIssuesData<T extends Exact<GetIssuesDataRequest, T>>(
  data: T,
  returnValue = false,
  setIssueRequestState?: (state: { loading: boolean; error: null | number }) => void,
) {
  return async (dispatch: AppDispatch) => {
    const response = await axiosWrapper.post<GetIssuesDataResponse>(`${getBackendUrl()}/issues/getIssuesData`, data);
    if (!response.data.status) {
      setIssueRequestState &&
        setIssueRequestState({
          loading: false,
          error: response.data.errorCode || errorCodes.SOMETHING_WENT_WRONG,
        });
      return;
    }
    if (response.data.result) {
      dispatch(setIssuesData(response.data.result));
      if (typeof response.data.count === "number") {
        dispatch(setIssuesCount(response.data.count));
      }
    }
    setIssueRequestState &&
      setIssueRequestState({
        loading: false,
        error: null,
      });
    if (returnValue) {
      return response.data.result;
    }
    // The issues were dispatched into the slice; a caller that did not ask for `returnValue` gets
    // nothing back.
    return undefined;
  };
}

export function fetchIssueById(issueId: NonNullable<IssueItem["id"]>) {
  return async (dispatch: AppDispatch): Promise<IssueItem | undefined> => {
    const result = await dispatch(getIssuesData({ issueId }, true));
    if (!Array.isArray(result)) return undefined;
    return result.find((item) => item.id === issueId) ?? result[0];
  };
}

export async function getIssuesList<T extends Exact<GetIssuesDataRequest, T>>(
  data: T,
): Promise<Record<string, unknown>[]> {
  const response = await axiosWrapper.post<GetIssuesDataResponse>(`${getBackendUrl()}/issues/getIssuesData`, data);
  if (!response.data.status) return [];
  // `result` is `.optional()` even on the success arm, which is what the `?? []` covers. The declared
  // `Record<string, unknown>[]` is the open shape the sole consumer (issuesPdf) re-narrows to its own
  // row type.
  return response.data.result ?? [];
}
export function getIssueViewpointData<T extends Exact<GetIssueViewpointDataRequest, T>>(data: T) {
  return async (dispatch: AppDispatch) => {
    const response = await axiosWrapper.post<GetIssueViewpointDataResponse>(
      `${getBackendUrl()}/issues/getIssueViewpointData`,
      data,
    );
    if (response.data.status && response.data.result) {
      dispatch(modifyIssuesData(response.data.result));
    }
  };
}
export function getfilteredIssuesData<T extends Exact<GetIssuesDataRequest, T>>(data: T, returnValue = false) {
  return async (dispatch: AppDispatch) => {
    const response = await axiosWrapper.post<GetIssuesDataResponse>(`${getBackendUrl()}/issues/getIssuesData`, data);
    if (!response.data.status) return;
    if (response.data.result) {
      dispatch(arrangeIssuesData(response.data.result));
      if (typeof response.data.count === "number") {
        dispatch(setIssuesCount(response.data.count));
      }
    }
    if (returnValue) {
      return response.data.result;
    }
    // Same as `getIssuesData`: the rows went into the slice unless `returnValue` was asked for.
    return undefined;
  };
}
export function getIssuesPinData<T extends Exact<GetIssuesPinDataRequest, T>>(data: T) {
  return async (dispatch: AppDispatch) => {
    const response = await axiosWrapper.post<GetIssuesPinDataResponse>(
      `${getBackendUrl()}/issues/getIssuesPinData`,
      data,
    );
    if (response.data.status && response.data.result) {
      dispatch(setIssuesPinData(response.data.result));
    }
  };
}

export function getIssuesDataByPortal<T extends Exact<GetIssuesDataByPortalRequest, T>>(data: T) {
  return (dispatch: AppDispatch) => {
    dispatch(setItemsAreLoading(true));
    axiosWrapper
      .post<GetIssuesDataByPortalResponse>(`${getBackendUrl()}/issues/getIssuesDataByPortal`, data)
      .then((response) => {
        if (response.data.status && response.data.result) {
          dispatch(setIssuesData(response.data.result));
        }
      })
      .catch((error) => {
        dispatch(setItemsHaveError(true));
        // deliberately floating: re-surfaces `error` as an unhandled rejection after this .catch consumed the original
        // (see saveCommentsData).
        // Reported, not re-thrown. `void Promise.reject(...)` was wrong twice over: it creates a
        // NEW rejection whose stack points here rather than at the real failure, and it trips this
        // repo's own `catch-handler/require-promise-reject` rule, which accepts captureException.
        Sentry.captureException(error);
      });
  };
}
export async function getCompleteIssuesDataByPortal<T extends Exact<GetIssuesDataByPortalRequest, T>>(data: T) {
  const response = await axiosWrapper.post<GetIssuesDataByPortalResponse>(
    `${getBackendUrl()}/issues/getIssuesDataByPortal`,
    data,
  );
  if (!response.data.status) return [];
  return response.data.result ?? [];
}
export function getProjectsStatusesData() {
  return (dispatch: AppDispatch) => {
    dispatch(setItemsAreLoading(true));
    axiosWrapper
      .get<GetAllStatusesForProjectsResponse>(`${getBackendUrl()}/projects/getAllStatusesForProjects`)
      .then((response) => {
        if (response.data.status) {
          dispatch(setProjectsStatusesData(response.data.result));
        }
      })
      .catch((error) => {
        dispatch(setItemsHaveError(true));
        // deliberately floating: re-surfaces `error` as an unhandled rejection after this .catch consumed the original
        // (see saveCommentsData).
        // Reported, not re-thrown. `void Promise.reject(...)` was wrong twice over: it creates a
        // NEW rejection whose stack points here rather than at the real failure, and it trips this
        // repo's own `catch-handler/require-promise-reject` rule, which accepts captureException.
        Sentry.captureException(error);
      });
  };
}
// KNOWN BUG: the success toast below is unconditional theatre — POST /projects/saveProjectStatus is a
// server-side no-op stub, so this thunk tells the user "Status changed successfully" for a change that was
// never written, and the project's status is unchanged on the next load. Evidence: the wired handler is
// packages/server/projects/project.service.ts:185 (saveProjectStatus), whose entire body is
// `return Promise.resolve({ status: true })` — no DB write, no permission gate — so `response.data.status`
// is true for every request, including one with a bogus id or status. The fix here would be to drop the
// toast (or this thunk), but that only becomes correct once the product decides whether the endpoint should
// persist a status or be retired in favour of /updateProjectsData (which does write `statusId`).
// As of 2026-09-02 no component dispatches this thunk — it is exported but unreferenced across
// packages/web/src and packages/mobile — so wire it up only after the server side actually writes.
export function saveProjectStatus<T extends Exact<SaveProjectStatusRequest, T>>(data: T) {
  return (dispatch: AppDispatch) => {
    axiosWrapper
      .post<SaveProjectStatusResponse>(`${getBackendUrl()}/projects/saveProjectStatus`, data)
      .then((response) => {
        if (response.data.status) {
          dispatch(
            showToastNotification({
              content: t("Status changed successfully"),
            }),
          );
        }
      })
      .catch((error) => {
        dispatch(setItemsHaveError(true));
        // deliberately floating: re-surfaces `error` as an unhandled rejection after this .catch consumed the original
        // (see saveCommentsData).
        // Reported, not re-thrown. `void Promise.reject(...)` was wrong twice over: it creates a
        // NEW rejection whose stack points here rather than at the real failure, and it trips this
        // repo's own `catch-handler/require-promise-reject` rule, which accepts captureException.
        Sentry.captureException(error);
      });
  };
}
export function saveProjectAttachedPositionData(data: Record<string, unknown>) {
  return async (dispatch: AppDispatch) => {
    const response = await axiosWrapper.post<SaveProjectAssignedPositionResponse>(
      `${getBackendUrl()}/projects/saveProjectAssignedPosition`,
      data,
    );
    let toastFields;
    if (response.data.status) {
      toastFields = {
        content: t("Assigned position changed successfully"),
        type: TOAST_TYPE_SUCCESS,
      };
    } else {
      toastFields = {
        content: untranslated(response.data.errorMessage || SOMETHING_WENT_WRONG),
        type: TOAST_TYPE_ERROR,
      };
    }
    dispatch(showToastNotification(toastFields));
  };
}
export function saveTaskStatus<T extends Exact<SaveTaskStatusRequest, T>>(data: T) {
  return async (dispatch: AppDispatch) => {
    const response = await axiosWrapper.post<SaveTaskStatusResponse>(`${getBackendUrl()}/tasks/saveTaskStatus`, data);
    if (response.data.status) {
      dispatch(updateTaskData(data));
    }
    return response.data;
  };
}
export async function deleteTask<T extends Exact<DeleteTaskRequest, T>>(data: T) {
  const response = await axiosWrapper.post<DeleteTaskResponse>(`${getBackendUrl()}/tasks/deleteTask`, data);
  return response;
}
export async function saveProjectData<T extends Exact<SaveProjectsRequest, T>>(data: T): Promise<SaveProjectsResponse> {
  const response = await axiosWrapper.post<SaveProjectsResponse>(`${getBackendUrl()}/projects/saveProjectsData`, data);
  return response.data;
}
export async function getPdfMarkUpData<T extends Exact<GetPdfMarkUpDataRequest, T>>(data: T) {
  const response = await axiosWrapper.post<GetPdfMarkUpDataResponse>(
    `${getBackendUrl()}/issues/getPdfMarkUpData`,
    data,
  );
  return response;
}
export function successReadNotificationToast(selectedNotificationsId: string[]) {
  // Promise-returning without `async`: the thunk has nothing to await, but the promise AND the isolation
  // `async` gave it both have to stay. Every call site fire-and-forgets this as
  // `void dispatch(successReadNotificationToast(...))` from inside an async handler, and redux-thunk
  // invokes the thunk synchronously inside `dispatch`. A bare `return Promise.resolve()` after the body
  // would let a throw out of `t()` or the toast reducer unwind through `dispatch(...)` and abort the REST
  // of the caller's handler. `Promise.resolve().then(...)` reproduces the `async` behaviour exactly: the
  // body runs detached from the caller's stack and a throw rejects only this promise.
  return (dispatch: AppDispatch): Promise<void> =>
    Promise.resolve().then(() => {
      const suffix = selectedNotificationsId.length > 1 ? "s" : "";
      dispatch(
        showToastNotification({
          content: t(`Notification${suffix} successfully marked as read`),
          type: TOAST_TYPE_SUCCESS,
        }),
      );
    });
}
export async function getCommentsList<T extends Exact<GetIssueCommentsRequest, T>>(
  data: T,
): Promise<{ data: GetCommentsResponse }> {
  return await axiosWrapper.post<GetCommentsResponse>(`${getBackendUrl()}/comments/getIssueComments`, data);
}
async function getIssuesIdToExport(data: GetIssuesIdToExportRequest) {
  return await axiosWrapper.post<GetIssuesIdToExportResponse>(`${getBackendUrl()}/issues/getIssuesIdToExport`, data);
}
export async function handleIssuesExportAsPDF({ projectId, issuesIds }: ExportParams): Promise<StatusResponse | void> {
  if (!issuesIds) {
    const res = await getIssuesIdToExport({ projectId });
    if (res.data.status) {
      issuesIds = res.data.result;
    } else {
      return res.data;
    }
  }
  if (issuesIds?.length) {
    // SEQUENCING: awaited so this async function only resolves once the export has actually finished.
    // Unawaited it resolved immediately, so any caller that awaits it (to hide a spinner, or to read the
    // StatusResponse the other branch returns) was told the export was done before it had started.
    // `exportPdf` reports its own failures via a toast and resolves with a status, so this cannot reject.
    await exportPdf({ issuesIds, exportType: EXPORT_TYPE.PDF });
  } else {
    return {
      status: false,
      errorMessage: "There are no issues in this Project",
    };
  }
}
export const issuesBcfImport = async <T extends Exact<SaveIssuesDataFromBCFRequest, T>>(data: T) => {
  const response = await axiosWrapper.post<SaveIssuesDataFromBCFResponse>(
    `${getBackendUrl()}/issues/saveIssuesDataFromBCF`,
    data,
  );
  return response;
};
export const getDefaultListOfStatusAvailable = async ({
  spaceId,
  entityType,
}: {
  spaceId: string;
  entityType: string;
}) => {
  const data = {
    value: {
      params: {
        entityType,
        spaceId,
      },
      entityType,
      spaceId,
    },
  };
  return await axiosWrapper.post<FetchDefaultListOfStatusesAvailableResponse>(defaultListOfStatusAvailableUrl, data);
};

/**
 * True when the calling user holds `requiredPermissionId` on this issue.
 *
 * `userPermissions` — `$addFields`-ed onto every row by `getUserPermissionPipeline` rather than stored —
 * is declared as `issueItemShape.userPermissions` (`z.array(z.number()).optional()`,
 * server/issues/issues.schema.ts:220), so the parameter names what it needs: every caller passes something
 * carrying that member (the redux slice's `IssueItem`, `LoadedIssue`, or `IssueGridRow`, which Picks it),
 * and the `| undefined` covers the record lookups that may miss.
 *
 * The `Array.isArray` guard is kept deliberately, so a row whose `userPermissions` is not an array still
 * answers `false` rather than throwing.
 */
export const isIssueRequiredPermission = (
  issuesData: Pick<IssueItem, "userPermissions"> | undefined,
  requiredPermissionId: number,
): boolean => {
  const userPermissions = issuesData?.userPermissions;
  return Array.isArray(userPermissions) && userPermissions.includes(requiredPermissionId);
};
